
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

export interface ModalItem {
	title: string;
	key: string;
	dataType: string;
	readOnly?: boolean;
	newOnly?: boolean;
	updateOnly?: boolean;
	data?: any;
	placeholder?: string;
	required?: boolean;
	customPermissions?: any[];
	useCustomRender?: boolean;
	maxLength?: number;
	minLength?: number;
	order?: number;
	defaultValue?: any;
	props?: any;
	visible?: any;
	description?: string;
	max?: number | null;
	min?: number | null;
	linkedKey?: any | null;
}
const GenericTable = namespace("GenericTable");

@Component({
	components: {
		"v-select": vSelect,
		scrollbar: VuePerfectScrollbar
	}
})
export default class GenericUpdateModal extends Vue {
	$refs!: {
		genericUpdateModal: any;
	};

	@Prop() public deletePermissions!: any[];
	@Prop() public modalItems!: ModalItem[];
	@Prop() public dataList!: any[];
	@Prop() public term!: string;
	@Prop() public title!: string;
	@Prop() public rightSubTitle: string = "";

	@Prop() public onSave;
	@Prop() public onDelete;
	@Prop() public onAddNew;
	@Prop({ default: "lg" }) modalSize!: string;
	@Mutation private setIsLoading: (loading: boolean) => void;

	@GenericTable.Getter getModalRow: any;
	@GenericTable.Mutation setModalRow: any;

	index: number = null;
	isAddingNew: boolean = false;
	showRightAlignText: boolean = false;
	isShown: boolean = false;
	readOnly: boolean = false;
	isValid: boolean = false;
	hasChanged: boolean = false;
	isConfirmationShown: boolean = false;
	isCloseConfirmationShown: boolean = false;
	invalidComponents: string[] = [];
	private componentsLoaded: number = 0;

	public newEntry!: any;
	// == Methods == //
	public showUpdateDialog(dataRow?, readOnly?, addNew?) {
		this.componentsLoaded = 0;
		if (readOnly) {
			this.readOnly = true;
		} else {
			this.readOnly = false;
		}
		if (addNew) {
			this.isAddingNew = true;
			this.isValid = false;
			this.hasChanged = false;
		} else {
			this.isAddingNew = false;
		}
		if (dataRow) {
			this.index = this.dataList.findIndex(x => x === dataRow);
			this.setModalRow(dataRow);
		} else {
			this.index = -1;
			this.setModalRow({});
		}
		this.isShown = true;

		if (this.numberOfVisibleComponents === 0) {
			this.setIsLoading(false);
		}

		// Update our object in the store
		this.setupEntry();
	}

	private componentLoaded(): void {
		++this.componentsLoaded;

		if (this.componentsLoaded === this.numberOfVisibleComponents) {
			this.setIsLoading(false);
		}
	}

	public hideUpdateDialog(confirmed) {
		if (this.hasChanged && !confirmed) {
			// confirmation
			this.isShown = true;
			this.isCloseConfirmationShown = true;
		} else {
			this.isShown = false;
			this.isConfirmationShown = false;
			this.isCloseConfirmationShown = false;
			this.index = -1;
		}
	}

	private get numberOfVisibleComponents(): number {
		if (!this.modalItems || this.modalItems.length === 0) {
			return 0
		}

		return this.getModalItems().filter(item => item.dataType === "component").length;
	}

	private showCloseConfirmationDialog() {
		this.isCloseConfirmationShown = true;
	}

	private hideCloseConfirmationDialog() {
		this.isCloseConfirmationShown = false;
	}

	private showDeleteConfirmationDialog() {
		this.isConfirmationShown = true;
	}

	private hideDeleteConfirmationDialog() {
		this.isConfirmationShown = false;
	}

	private confirmSave(itemToUpdate) {
		this.onSave(itemToUpdate);
		this.hideUpdateDialog(true);
	}

	private confirmAdd(itemToAdd) {
		this.onAddNew(itemToAdd);
		this.hideUpdateDialog(true);
	}

	public validate() {
		let row = this.newEntry;
		for (var item in row) {
			let header = this.modalItems.find(modalItem => modalItem.key == item);
			if (header && header.hasOwnProperty("max") && header.hasOwnProperty("dataType") && header.dataType == "number" ) {
					if (Number(row[item]) > header.max) {
						return false;
					}
				}

			if (header && header.hasOwnProperty("min") && header.hasOwnProperty("dataType") && header.dataType == "number" ) {
				if (Number(row[item]) < header.min) {
					return false;
				}
			}

			if (header && header.required) {
				if (header.newOnly && header.newOnly == this.isAddingNew && !row[item]) {
					return false;
				} else if (!header.newOnly && !row[item]) {
					return false;
				}
			}

			if (header && header.hasOwnProperty("minLength") && header.hasOwnProperty("dataType") && header.dataType == "text"){
				if(row[item].length < header.minLength) return false;
			}
		}

		//Check if we still have any invalid components
		if(this.invalidComponents && this.invalidComponents.length > 0)
		return false;

		return true;
	}

	//Keeps track of which components are invalid
	private validateComponent(component, value) {
		if (this.invalidComponents && this.invalidComponents.filter(x => x == component.key).length > 0) {
		if(value) 
		{
				//if the key is there and no longer invalid filter out from array
				this.invalidComponents = this.invalidComponents.filter(x => x !== component.key);
			}
	}				
	else
		if(!value)
			this.invalidComponents.push(component.key);
	}

	private confirmDelete(itemToDelete) {
		this.onDelete(itemToDelete);
		this.hideUpdateDialog(true);
	}

	private setupEntry() {
		if (this.index == -1 && this.isAddingNew) {
			this.newEntry = {};
			for (var i in this.modalItems) {
				const modelItem = this.modalItems[i];
				this.newEntry[modelItem.key] = modelItem.defaultValue ? modelItem.defaultValue : null;
			}
		} else {
			const newEntry = {};
			for (var key in this.dataList[this.index]) {
				const data = this.dataList[this.index][key];

				if (typeof data === "object") {
					if (Array.isArray(data)) {
						// Deep clone array contents
						const dataCopy = [];
						data.forEach(item => {
							if (typeof item === "object") {
								const itemClone = (Vue as any).util.extend({}, item);
								dataCopy.push(itemClone);
							} else {
								dataCopy.push(item);
							}
						});
						newEntry[key] = dataCopy;
					} else if (!data) {
						newEntry[key] = data;
					} else {
						// Deep clone object
						newEntry[key] = (Vue as any).util.extend({}, data);
					}
				} else {
					newEntry[key] = data;
				}
			}

			this.newEntry = newEntry;
			this.hasChanged = false;
		}
	}

	@Watch("newEntry", { deep: true })
	public entryChanged() {
		this.isValid = this.validate();
		this.hasChanged = this.checkHasChanged();
		this.setModalRow({ ...this.newEntry });
	}

	// Returns the value of the linked Key
	private linkedKeyValue(modalItem: ModalItem): any {
		if (modalItem.linkedKey) {
			//Check that the linkedKey has a modal Item
			let linkedModalItem = this.modalItems.find(x => x.key == modalItem.linkedKey);
			if (linkedModalItem) {
				let newValue = this.newEntry[linkedModalItem.key];
				return newValue;
			}
		}
		return null;
	}

	@Watch("newEntry", { deep: true })
	public getRow() {
		return this.newEntry;
	}

	created() {
		this.newEntry = {};
		for (var i in this.modalItems) {
			Vue.set(this.newEntry, this.modalItems[i].key, "null");
		}
	}

	@Watch("getModalRow", { immediate: true, deep: true })
	private watchStore() {
		if (this.getRow() != null) {
			this.isValid = this.validate();
			this.hasChanged = this.checkHasChanged();
		}
	}

	@Watch("newEntry", { deep: true })
	private checkHasChanged() {
		if (this.index != -1) {
			for (var key in this.dataList[this.index]) {
				let valueA = this.newEntry[key];
				let valueB = this.dataList[this.index][key];
				if (valueA != null && valueB != null) {
					if (typeof valueA === "object") {
						if (JSON.stringify(valueA) !== JSON.stringify(valueB)) {
							return true;
						}
					} else {
						if (valueA.toString() != valueB.toString()) {
							return true;
						}
					}
				} else if (valueA != valueB) {
					return true;
				}
			}
		}
		return false;
	}

	private isEquivalent(a, b) {
		// Create arrays of property names
		var aProps = Object.getOwnPropertyNames(a);
		var bProps = Object.getOwnPropertyNames(b);

		// If number of properties is different,
		// objects are not equivalent
		if (aProps.length != bProps.length) {
			return false;
		}

		for (var i = 0; i < aProps.length; i++) {
			var propName = aProps[i];

			// If values of same property are not equal,
			// objects are not equivalent
			if (a[propName] !== b[propName]) {
				return false;
			}
		}

		// If we made it this far, objects
		// are considered equivalent
		return true;
	}

	private getModalItems() {
		let items = [];
		if (this.isAddingNew) {
			items = this.modalItems.filter(item => item.updateOnly != true);
		} else {
			items = this.modalItems.filter(item => item.newOnly != true);
		}
		items.sort(function(a, b) {
			return a.order - b.order;
		});

		// only show inputs that are marked as visible or with the value not set
		items = items.filter(item => item.visible || item.visible == undefined);

		return items;
	}

	private getSelectedModalItem(item, id) {
		let value = null;
		if (item && item.data && id) {
			value = item.data.find(i => {
				return i[item.key] == id;
			});
		}
		return value;
	}

	private checkPermissions(required, current) {
		if (required == null || this.isAddingNew) {
			return true;
		}

		// no permissions were passed in
		if (!current) {
			return false;
		}
		return required.some(r => Object.keys(current).includes(r));
	}

	private componentItemChanged(newValue: any) {
		this.isValid = this.validate();
		this.hasChanged = this.checkHasChanged();
		this.setModalRow({ ...this.newEntry });
	}
}
